import { RefSelectProps, Select, SelectProps } from 'antd';
import { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import { HTMLAttributes, KeyboardEvent, ReactElement, Ref, useMemo } from 'react';
import useReadonly from 'src/hook/useReadonly';
import { DomUtil } from 'src/util/DomUtil';
import OptionDetail from './OptionDetail';
import { detailByValue, estimatePopupWidth, optionDetail } from './optionMeta';
import "./select.less";

const DEFAULT_SHOW_SEARCH: SelectProps['showSearch'] = { optionFilterProp: 'label' } as const;

let measureCanvas: CanvasRenderingContext2D | null | undefined;

/**
 * Canvas text measurement: exact, and unlike a DOM probe it neither renders nor reflows.
 * The canvas is reused but the font is re-read per call - caching it would freeze whatever
 * `document.body` happened to compute to at the time of the first measurement.
 */
function getMeasureContext(): CanvasRenderingContext2D | null {
  measureCanvas ??= document.createElement('canvas').getContext('2d');
  if (measureCanvas) {
    const { fontWeight, fontSize, fontFamily } = window.getComputedStyle(document.body);
    measureCanvas.font = `${fontWeight} ${fontSize} ${fontFamily}`;
  }
  return measureCanvas;
}

function popupWidthOf(options: SelectProps['options']): number | undefined {
  const ctx = options?.length ? getMeasureContext() : null;
  if (!ctx) return;
  return estimatePopupWidth(options, text => ctx.measureText(text).width);
}

/**
 * Resolved containers, keyed by trigger.
 *
 * rc-portal re-resolves the container after *every* portal render, not once per open: its
 * effect has no dependency array (Portal.js) and rc-trigger hands it a fresh closure each
 * render (Popup/index.js). Without this cache the whole ancestor walk - `closest` chains,
 * getComputedStyle up to the root - reran on every reposition, i.e. on every scroll and
 * resize with a dropdown open.
 *
 * A WeakMap lets a detached trigger be collected, and `isConnected` guards the case where
 * the resolved container itself was replaced (a table row re-rendering, say).
 */
const containerCache = new WeakMap<HTMLElement, HTMLElement>();

/** Cached {@link DomUtil.getPopupContainer}. */
const getPopupContainer: SelectProps['getPopupContainer'] = (triggerNode: HTMLElement): HTMLElement => {
  const cached = containerCache.get(triggerNode);
  if (cached?.isConnected) return cached;

  const container = DomUtil.getPopupContainer(triggerNode);
  containerCache.set(triggerNode, container);
  return container;
};

/**
 * AntD Select with some default values
 * @param props AntD Select properties
 * @param ignoreReadonly optional disable of readonly behavior
 * @param ref optional ref forwarded to the AntD Select (React 19 ref-as-prop)
 * @returns
 */
export const XmSelect: <ValueType = unknown, OptionType extends DefaultOptionType | BaseOptionType = DefaultOptionType>(
  props: SelectProps<ValueType, OptionType> & { ignoreReadonly?: boolean; ref?: Ref<RefSelectProps>; }
) => ReactElement = ({ ignoreReadonly, disabled, showSearch, ...props }) => {
  const { isDisabled } = useReadonly(ignoreReadonly, disabled);
  const popupWidth = useMemo(() => popupWidthOf(props.options), [props.options]);
  const details = useMemo(() => detailByValue(props.options, props.fieldNames?.value), [props.options, props.fieldNames?.value]);
  const optionRender: SelectProps['optionRender'] = useMemo(() => details.size
    ? option => <OptionDetail label={option.label || option.value} detail={optionDetail(option.data)} />
    : undefined, [details]);
  const labelRender: SelectProps['labelRender'] = useMemo(() => details.size
    ? ({ label, value }) => <OptionDetail label={label} detail={details.get(value)} />
    : undefined, [details]);
  const captureProps: Pick<HTMLAttributes<HTMLDivElement>, 'onKeyDownCapture'> = {
    onKeyDownCapture: (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key !== 'Tab' || !props.mode || !e.currentTarget.classList.contains('ant-select-open')) return;
      e.stopPropagation(); // rc-select maps Tab to select in an open list
    },
  };

  return <Select
    classNames={{ root: 'xm-select', popup: { root: 'xm-select-dropdown' } }}
    styles={{ content: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' } }}
    getPopupContainer={getPopupContainer}
    popupMatchSelectWidth={popupWidth}
    optionRender={optionRender}
    labelRender={labelRender}
    showSearch={showSearch == null || showSearch === true ? DEFAULT_SHOW_SEARCH
      : typeof showSearch === 'object' ? { ...DEFAULT_SHOW_SEARCH, ...showSearch }
        : showSearch}
    maxTagPlaceholder={omitted => `+${omitted.length}`}
    {...captureProps}
    {...props}
    disabled={isDisabled}
  />;
};
